// Simple voice service for orchestrated responses
// This handles turn-by-turn voice responses from the chat orchestrator

const ELEVENLABS_API_KEY = 'sk_6f556bde3bbc03e4115bd8e409d51fc8b7c122683f98a630';

interface Advisor {
  id: string;
  name: string;
  title: string;
  description?: string;
  personality_prompt?: string;
  elevenlabs_agent_id?: string;
  gender?: string;
  is_default?: boolean;
  voice_settings?: any;
}

interface VoiceResponse {
  persona_name: string;
  response_text: string;
}

/**
 * Get agent ID for advisor (consistent with main voice hook)
 */
function getAgentIdForAdvisor(advisor: Advisor): string {
  if (advisor.elevenlabs_agent_id && advisor.elevenlabs_agent_id.startsWith('agent_')) {
    return advisor.elevenlabs_agent_id;
  }
  
  const fallbackMapping: Record<string, string> = {
    'Beatrice': 'agent_01jz125yzgeyctcjc029ka5g76',
    'Samuel': 'agent_01jz112ggbfpps9df439se858p',
    'Leo': 'agent_01jz11nhmgf1hv7v63tt7kgjth',
    'Marcus': 'agent_01jz12119wfvdrn9kexm7mv0mr'
  };
  
  return fallbackMapping[advisor.name] || '';
}

/**
 * Execute a single voice turn for an advisor
 * Uses the agent's pre-configured personality (no system prompt override)
 */
export const speakTurn = async (
  advisor: Advisor, 
  textToSpeak: string, 
  userName: string
): Promise<void> => {
  return new Promise(async (resolve, reject) => {
    console.log(`🎤 Starting voice turn for ${advisor.name}`);
    
    let audioPlayer: HTMLAudioElement | null = null;
    let audioQueue: Blob[] = [];
    let isPlaying = false;
    let audioCompleted = false;

    const playNextInQueue = () => {
      if (isPlaying || audioQueue.length === 0) return;
      
      isPlaying = true;
      const audioBlob = audioQueue.shift()!;
      const audioUrl = URL.createObjectURL(audioBlob);
      audioPlayer = new Audio(audioUrl);
      
      audioPlayer.onended = () => {
        isPlaying = false;
        URL.revokeObjectURL(audioUrl);
        
        if (audioQueue.length > 0) {
          playNextInQueue();
        } else if (audioCompleted) {
          console.log(`✅ Voice turn completed for ${advisor.name}`);
          resolve();
        }
      };
      
      audioPlayer.onerror = (error) => {
        console.error('❌ Audio playback error:', error);
        isPlaying = false;
        reject(new Error('Audio playback failed'));
      };
      
      audioPlayer.play().catch(error => {
        console.error('❌ Audio play error:', error);
        isPlaying = false;
        reject(new Error('Could not play audio'));
      });
    };

    try {
      if (!ELEVENLABS_API_KEY || !ELEVENLABS_API_KEY.startsWith('sk_')) {
        throw new Error("ElevenLabs API key is not set or invalid");
      }

      const agentId = getAgentIdForAdvisor(advisor);
      if (!agentId) {
        throw new Error(`No ElevenLabs agent ID found for ${advisor.name}`);
      }

      console.log(`🤖 Using agent ${agentId} for ${advisor.name} (no system prompt override)`);
      
      const { Conversation } = await import('@elevenlabs/client');

      // Use the agent's pre-configured personality (no system prompt override)
      const conversation = await Conversation.startSession({
        agentId: agentId,
        requiresAuth: false,
        // No context or system prompt - let the agent use its configured personality
        
        onConnect: () => {
          console.log(`🎯 Voice session connected for ${advisor.name}`);
          // Send the message that this advisor should respond to
          conversation.sendUserMessage(textToSpeak);
        },
        
        onAudio: (audio) => {
          // Queue audio chunks
          audioQueue.push(new Blob([audio], { type: 'audio/mpeg' }));
          playNextInQueue();
        },
        
        onTurnEnd: async () => {
          console.log(`🏁 Voice turn completed for ${advisor.name}`);
          audioCompleted = true;
          
          // If no audio is currently playing and queue is empty, resolve immediately
          if (!isPlaying && audioQueue.length === 0) {
            await conversation.endSession();
            resolve();
          }
          // Otherwise, wait for audio to finish (handled in playNextInQueue)
        },
        
        onError: async (error) => {
          console.error(`❌ Voice error for ${advisor.name}:`, error);
          await conversation.endSession();
          reject(new Error(`Voice session failed for ${advisor.name}: ${error.message}`));
        },
        
        onDisconnect: () => {
          console.log(`🔌 Voice session disconnected for ${advisor.name}`);
        }
      });

      // Set a timeout as a safety net
      setTimeout(async () => {
        if (!audioCompleted) {
          console.warn(`⏰ Voice turn timeout for ${advisor.name}`);
          await conversation.endSession();
          reject(new Error(`Voice session timeout for ${advisor.name}`));
        }
      }, 30000); // 30 second timeout

    } catch (err) {
      console.error(`💥 Voice turn failed for ${advisor.name}:`, err);
      reject(err);
    }
  });
};

/**
 * Handle orchestrated voice responses (sequential turns)
 */
export const handleOrchestratorVoiceResponse = async (
  responses: VoiceResponse[],
  allAdvisorsInChat: Advisor[],
  userName: string,
  onSpeakerChange?: (speaker: string | null) => void,
  onError?: (error: string) => void
): Promise<void> => {
  console.log("🎭 Starting orchestrated voice sequence...");
  
  try {
    for (let i = 0; i < responses.length; i++) {
      const response = responses[i];
      const speaker = allAdvisorsInChat.find(a => a.name === response.persona_name);
      
      if (!speaker) {
        console.warn(`⚠️ Speaker ${response.persona_name} not found in chat participants`);
        continue;
      }

      try {
        console.log(`🎤 Executing turn ${i + 1}/${responses.length} for ${speaker.name}...`);
        
        onSpeakerChange?.(speaker.name);
        
        await speakTurn(speaker, response.response_text, userName);
        
        console.log(`✅ Turn for ${speaker.name} completed successfully`);
        
        // Add a small pause between speakers for natural pacing
        if (i < responses.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
      } catch (error) {
        console.error(`💥 Failed to speak turn for ${speaker.name}:`, error);
        onError?.(`Voice failed for ${speaker.name}: ${error.message}`);
        break;
      }
    }
    
    onSpeakerChange?.(null);
    console.log("🎉 Voice sequence completed successfully");
    
  } catch (error) {
    console.error('💥 Voice sequence failed:', error);
    onSpeakerChange?.(null);
    onError?.(`Voice sequence failed: ${error.message}`);
  }
};